import React, { ReactElement, useCallback, useContext, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { TranslationContext } from '@ds/react-utils';
import { ContainerLayout } from 'layouts';
import { FontBodyL } from 'components/shared';
import { useButtonsLoading } from 'hooks';
import { routes } from 'static/routes';
import VerifyApplicationContext from 'contexts/VerifyApplicationContext';
import { getEmailAddress, sendEmail, IEmailAddressResponse } from 'queries/EmailQueries';
import { AxiosError } from 'axios';
import replaceStarsWithBulletPoints from 'utils/replaceStarsWithBulletPoints/replaceStarsWithBulletPoints';

const SendEmailCodePage = (): ReactElement => {
    const context = useContext(VerifyApplicationContext);
    const history = useHistory();
    const translate = useContext(TranslationContext);

    const [emailAddress, setEmailAddress] = useState<IEmailAddressResponse | undefined>(undefined);
    const { isPrimaryButtonLoading, setIsPrimaryButtonLoading, areButtonsLoading } = useButtonsLoading();

    const onErrorCallback = (error: AxiosError) => {
        if (error?.response?.status === 401) {
            history.push(routes.SessionExpired);
        } else {
            history.push(routes.Error);
        }
    };

    useEffect(() => {
        getEmailAddress(
            (response: IEmailAddressResponse) => {
                setEmailAddress(response);
            },
            onErrorCallback
        );
    }, []);

    const onSendCodeClick = useCallback(() => {
        if (areButtonsLoading || !emailAddress) {
            return;
        }

        setIsPrimaryButtonLoading(true);

        sendEmail(
            {
                uniqueId: emailAddress.uniqueId,
                language: context.language
            },
            () => {
                setIsPrimaryButtonLoading(false);
                history.push(routes.ConfirmCode, { emailAddress });
            },
            (error: AxiosError) => {
                setIsPrimaryButtonLoading(false);
                onErrorCallback(error);
            }
        );
    }, [areButtonsLoading, setIsPrimaryButtonLoading, emailAddress, context.language, history]);

    return (
        <ContainerLayout>
            <ContainerLayout.Header>
                <ContainerLayout.Header.Title
                    title={translate("To keep you safe, confirm it's really you")}
                    titleDataQa="verify-send-email-code-title"
                />
            </ContainerLayout.Header>

            <ContainerLayout.InnerContent>
                <FontBodyL data-qa="verify-send-email-code-intro">
                    {translate("We'll send a code to")}
                </FontBodyL>
                <FontBodyL data-qa="verify-send-email-code-address">
                    <strong>{replaceStarsWithBulletPoints(emailAddress?.maskedEmail ?? '')}</strong>
                </FontBodyL>
            </ContainerLayout.InnerContent>

            <ContainerLayout.Footer>
                <ContainerLayout.Footer.Buttons>
                    <ContainerLayout.Footer.Buttons.Primary
                        data-qa="verify-send-email-code-send-button"
                        text={translate('Send Now')}
                        onClick={onSendCodeClick}
                        loading={isPrimaryButtonLoading}
                    />
                </ContainerLayout.Footer.Buttons>
            </ContainerLayout.Footer>
        </ContainerLayout>
    );
};

export default SendEmailCodePage;
